/**
 * MapLibre camera helpers — fitBounds and per-flight zoom utilities.
 * All functions are pure (no side effects beyond calling mapRef methods).
 * The mapRef holds a react-map-gl MapRef, which exposes the same camera API
 * as the underlying maplibre-gl Map instance.
 */

import type { MapRef } from 'react-map-gl/maplibre';
import type { MutableRefObject } from 'react';
import type { Flight, Message, Prediction, TeamPosition } from '../types/api';
import type { FlightPref } from '../store/appStore';
import { MAP_CONFIG } from '../constants/mapConfig';

type MapHolder = MutableRefObject<MapRef | null>;

type PaddingOptions = number | { top: number; bottom: number; left: number; right: number };

/**
 * Fits the map camera to a bounding box computed from `points`.
 * Expands the box by MAP_CONFIG.fitBoundsExpand on each side to add breathing room.
 * @param points  - array of [longitude, latitude] coordinate pairs
 * @param mapRef  - React ref holding the react-map-gl MapRef
 * @param padding - override the default uniform padding (supports per-side object for
 *                  asymmetric cases, e.g. when the side panel covers part of the map)
 */
export function fitBounds(
  points: [number, number][],
  mapRef: MapHolder,
  padding?: PaddingOptions,
): void {
  const map = mapRef.current;
  if (!map || points.length === 0) return;

  let minLng = points[0][0];
  let maxLng = points[0][0];
  let minLat = points[0][1];
  let maxLat = points[0][1];

  for (const [lng, lat] of points) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  const expand = MAP_CONFIG.fitBoundsExpand;
  const lngSpan = maxLng - minLng || expand;
  const latSpan = maxLat - minLat || expand;

  map.fitBounds(
    [
      [minLng - lngSpan * expand, minLat - latSpan * expand],
      [maxLng + lngSpan * expand, maxLat + latSpan * expand],
    ],
    {
      padding: padding ?? MAP_CONFIG.fitBoundsPaddingPx,
      duration: MAP_CONFIG.fitBoundsDurationMs,
    },
  );
}

/**
 * Fits the map to show every key point currently visible on the map for each
 * VISIBLE selected flight.
 *
 * Rules (matching §F of WEB_TRACKER_REFERENCE_MAP.md):
 * - Only flights with `flightPrefs[id].isVisible === true` contribute points.
 * - Departure = messages[flight.id]?.[0] (oldest message), NOT flight.lat/lng.
 * - Last known = messages[flight.id]?.at(-1).
 * - Normal/instant landing only if the corresponding <Marker> is currently
 *   rendered (same condition: isVisible && showNormal/showInstant && data exists).
 * - Team positions only if the flight's "Équipes" toggle is ON.
 * - If the side panel is open, the right-side padding is increased so no
 *   point ends up visually hidden behind the panel after the fit.
 * - No-op if there are no visible flights or no points loaded yet.
 *
 * @param selectedFlights - array of selected Flight objects
 * @param flightPrefs     - per-flight display preferences
 * @param messages        - record of message arrays keyed by flightId
 * @param teams           - record of team position arrays keyed by flightId
 * @param predictions     - record of prediction pairs keyed by flightId
 * @param isPanelOpen     - whether the side panel is currently expanded
 * @param panelWidth      - current side panel width in px (used for padding calc)
 * @param mapRef          - React ref holding the react-map-gl MapRef
 */
export function fitToSelection(
  selectedFlights: Flight[],
  flightPrefs: Record<number, FlightPref>,
  messages: Record<number, Message[]>,
  teams: Record<number, TeamPosition[]>,
  predictions: Record<number, { normal?: Prediction; instant?: Prediction }>,
  isPanelOpen: boolean,
  panelWidth: number,
  mapRef: MapHolder,
): void {
  const points: [number, number][] = [];

  for (const flight of selectedFlights) {
    const fp = flightPrefs[flight.id];
    if (!fp?.isVisible) continue; // skip hidden flights

    const msgs = messages[flight.id];
    if (msgs?.length) {
      points.push([msgs[0].longitude, msgs[0].latitude]);         // departure (oldest message)
      const last = msgs.at(-1)!;
      points.push([last.longitude, last.latitude]);               // last known position
    }

    // Normal landing marker — same condition as FlightLayers render: isVisible && showNormal && pn
    const pn = predictions[flight.id]?.normal;
    if (fp.showNormal && pn) {
      points.push([pn.longitude, pn.latitude]);
    }

    // Instant landing marker — same condition: isVisible && showInstant && pi
    const pi = predictions[flight.id]?.instant;
    if (fp.showInstant && pi) {
      points.push([pi.longitude, pi.latitude]);
    }

    // Team positions — only when the "Équipes" toggle is on
    if (fp.showTeams) {
      for (const t of teams[flight.id] ?? []) {
        if (t.longitude != null && t.latitude != null) {
          points.push([t.longitude, t.latitude]);
        }
      }
    }
  }

  // No-op: no visible flights, or messages not yet loaded after first selection
  if (points.length === 0) return;

  // Asymmetric padding: the side panel (right side) covers part of the map when open.
  // Points near the right edge would be mathematically inside the bbox but visually
  // hidden under the panel — increase right padding by the panel width to compensate.
  const padding = {
    top:    40,
    bottom: 40,
    left:   40,
    right:  40 + (isPanelOpen ? panelWidth : 0),
  };

  fitBounds(points, mapRef, padding);
}

/**
 * Zooms to the balloon's last known position.
 * Effective zoom = 14 / MAP_CONFIG.singlePointZoomFactor (factor > 1 zooms out further).
 * @param flightId - target flight
 * @param messages - record of message arrays keyed by flightId
 * @param mapRef   - React ref holding the react-map-gl MapRef
 */
export function zoomToBalloon(
  flightId: number,
  messages: Record<number, Message[]>,
  mapRef: MapHolder,
): void {
  const map = mapRef.current;
  const last = messages[flightId]?.at(-1);
  if (!map || !last) return;

  map.easeTo({
    center:   [last.longitude, last.latitude],
    zoom:     14 / MAP_CONFIG.singlePointZoomFactor,
    duration: MAP_CONFIG.fitBoundsDurationMs,
  });
}

/**
 * Zooms to the normal landing prediction.
 * Effective zoom = 11 / MAP_CONFIG.singlePointZoomFactor.
 * @param flightId    - target flight
 * @param predictions - record of prediction pairs keyed by flightId
 * @param mapRef      - React ref holding the react-map-gl MapRef
 */
export function zoomToNormalLanding(
  flightId: number,
  predictions: Record<number, { normal?: Prediction; instant?: Prediction }>,
  mapRef: MapHolder,
): void {
  const map = mapRef.current;
  const pred = predictions[flightId]?.normal;
  if (!map || !pred) return;

  map.easeTo({
    center:   [pred.longitude, pred.latitude],
    zoom:     11 / MAP_CONFIG.singlePointZoomFactor,
    duration: MAP_CONFIG.fitBoundsDurationMs,
  });
}

/**
 * Zooms to the instant landing prediction.
 * Effective zoom = 11 / MAP_CONFIG.singlePointZoomFactor.
 * @param flightId    - target flight
 * @param predictions - record of prediction pairs keyed by flightId
 * @param mapRef      - React ref holding the react-map-gl MapRef
 */
export function zoomToInstantLanding(
  flightId: number,
  predictions: Record<number, { normal?: Prediction; instant?: Prediction }>,
  mapRef: MapHolder,
): void {
  const map = mapRef.current;
  const pred = predictions[flightId]?.instant;
  if (!map || !pred) return;

  map.easeTo({
    center:   [pred.longitude, pred.latitude],
    zoom:     11 / MAP_CONFIG.singlePointZoomFactor,
    duration: MAP_CONFIG.fitBoundsDurationMs,
  });
}

/**
 * Fits the map to encompass the full trajectory for a single flight.
 * Only includes the actual message coordinates — NOT prediction landing zones.
 * (The button label is "Trajectoire", not "Tout afficher".)
 * @param flightId - target flight
 * @param messages - record of message arrays keyed by flightId
 * @param mapRef   - React ref holding the react-map-gl MapRef
 */
export function zoomToTrajectory(
  flightId: number,
  messages: Record<number, Message[]>,
  mapRef: MapHolder,
): void {
  const points: [number, number][] = (messages[flightId] ?? []).map(
    (m) => [m.longitude, m.latitude],
  );
  fitBounds(points, mapRef);
}
